// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2)
// version  : 3.1
// updated  : 2026-07-25
// protected: NO  -> D-022B/D-028 Reset Data Simulasi (UAT). DESTRUCTIVE; Super Owner + ketik RESET.
// changelog: D-028 — reset aktif di production tanpa env flag; frasa "RESET".
//            UAT-FIX-2 FIX-C — konfirmasi BERLAPIS: ketik RESET -> dialog ringkasan dampak NYATA
//            (Saldo Awal/item/invoice/WO/surat jalan + jumlah PDF/objek R2 dari /preview) ->
//            "Ya, Hapus Permanen". UAT-FIX-3 B: master PRODUK kini IKUT dihapus (diinput ulang);
//            hanya master DISKON yang dipertahankan. Dialog menampilkan jumlah Master Produk.
// =============================================================================
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AlertTriangle, Loader2, RotateCcw, ShieldCheck, FileText, Package,
  Receipt, Wrench, Truck, Boxes, Shirt,
} from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { EmptyState, TableSkeletonRows, useDelayedLoading } from "@/v2/components/ux";

const CONFIRM_PHRASE = "RESET";

export default function UatResetPage() {
  const { api } = useV2Auth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v2/admin/reset-simulation/preview");
      if (!r.ok) { toast.error("Gagal memuat preview"); return; }
      setData(await r.json());
    } catch (e) {
      toast.error("Gagal memuat preview");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const doReset = async () => {
    setBusy(true);
    try {
      const r = await api("/api/v2/admin/reset-simulation", {
        method: "POST", body: JSON.stringify({ confirm, scope: "transactions" }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(typeof j.detail === "string" ? j.detail : "Reset gagal"); return; }
      const pdfDel = j.archive_purge_r2?.deleted ?? 0;
      toast.success(
        `Reset selesai oleh ${j.executed_by}: ${j.total_rows_deleted ?? 0} baris + ${pdfDel} objek arsip R2 dihapus.`
      );
      setConfirm("");
      setDlgOpen(false);
      await load();
    } catch (e) {
      toast.error("Reset gagal");
    } finally {
      setBusy(false);
    }
  };

  const showSkeleton = useDelayedLoading(loading);
  if (loading) {
    return showSkeleton ? (
      <div className="space-y-6 max-w-3xl" data-testid="uat-reset-loading">
        <div className="h-8 w-64 rounded-md bg-primary/10 animate-pulse" />
        <Card className="overflow-hidden">
          <Table><TableBody><TableSkeletonRows rows={5} cols={2} testid="uat-reset-skeleton" /></TableBody></Table>
        </Card>
      </div>
    ) : null;
  }
  if (!data) return null;

  const canReset = confirm === CONFIRM_PHRASE && !busy && data.reset_enabled !== false;
  const totalRows = Object.values(data.resettable || {}).reduce((a, b) => a + b, 0);
  const disabled = data.reset_enabled === false;
  const rt = data.resettable || {};
  const archiveCount = data.archive_objects_r2 ?? 0;
  const HIGHLIGHTS = [
    { key: "products", label: "Master Produk", icon: Shirt },
    { key: "opening_balance_batch", label: "Saldo Awal (batch)", icon: Boxes },
    { key: "items", label: "Item / QR", icon: Package },
    { key: "invoices", label: "Invoice", icon: Receipt },
    { key: "work_orders", label: "Work Order", icon: Wrench },
    { key: "shipments", label: "Surat Jalan", icon: Truck },
  ];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="uat-reset-page">
      {data.reset_active_in_production && (
        <Card className="p-4 border-destructive bg-destructive/10 flex items-start gap-3" data-testid="uat-reset-prod-warning">
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-destructive">PERHATIAN: Reset Data masih AKTIF di PRODUCTION.</p>
            <p className="text-sm text-muted-foreground">
              Setelah UAT selesai, segera set <code>RESET_DATA_ENABLED=false</code> untuk mematikannya permanen.
            </p>
          </div>
        </Card>
      )}
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <RotateCcw className="w-6 h-6 text-destructive" />Reset Data Simulasi
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Membersihkan data uji coba operasional sebelum UAT. Pengguna, role matrix, Owner,
          backup, dan konfigurasi <b>tidak</b> dihapus. Hanya <b>Super Owner</b> yang dapat menjalankan.
        </p>
      </div>

      <Card className="p-4 flex flex-wrap items-center gap-3" data-testid="uat-reset-status">
        {disabled ? (
          <>
            <Badge variant="destructive" className="gap-1" data-testid="uat-reset-disabled-badge">
              <AlertTriangle className="w-3.5 h-3.5" />Dinonaktifkan
            </Badge>
            <span className="text-sm text-muted-foreground" data-testid="uat-reset-disabled-reason">
              {data.disabled_reason || "Reset Data dinonaktifkan (mode real production)."}
            </span>
          </>
        ) : (
          <>
            <Badge variant="secondary" className="gap-1">
              <ShieldCheck className="w-3.5 h-3.5" />Aktif (Super Owner)
            </Badge>
            <span className="text-sm text-muted-foreground">
              Ketik <code>{CONFIRM_PHRASE}</code> lalu klik tombol untuk menjalankan.
            </span>
          </>
        )}
      </Card>

      <Card className="overflow-hidden">
        <div className="px-4 py-3 border-b text-sm font-medium flex items-center justify-between gap-2 flex-wrap">
          <span>Data yang akan direset</span>
          <div className="flex items-center gap-2">
            <Badge variant="outline" data-testid="uat-total-rows">{totalRows} baris</Badge>
            <Badge variant="outline" className="gap-1" data-testid="uat-archive-count">
              <FileText className="w-3.5 h-3.5" />{archiveCount} PDF/objek R2
            </Badge>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Tabel / Modul</TableHead><TableHead className="text-right">Jumlah Baris</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {Object.keys(data.resettable || {}).length === 0 && (
              <TableRow><TableCell colSpan={2} className="p-0">
                <EmptyState icon={ShieldCheck} title="Tidak ada data untuk direset"
                  description="Semua data simulasi operasional sudah bersih. Data baru akan muncul di sini setelah ada transaksi (Saldo Awal, WO, invoice, dsb.)."
                  testid="uat-reset-empty" />
              </TableCell></TableRow>
            )}
            {Object.entries(data.resettable || {}).map(([t, n]) => (
              <TableRow key={t} className="hover:bg-muted/50 transition-colors">
                <TableCell className="font-mono text-xs">{t}</TableCell>
                <TableCell className="text-right">{n}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 text-xs text-muted-foreground space-y-1" data-testid="uat-kept-list">
        <div className="font-medium text-foreground">Tidak akan dihapus:</div>
        <div className="flex flex-wrap gap-1.5">
          {(data.kept_tables || []).map((t) => (
            <Badge key={t} variant="outline" className="font-mono">{t}</Badge>
          ))}
        </div>
      </Card>

      {disabled ? (
        <Card className="p-5 border-muted bg-muted/30 space-y-2" data-testid="uat-reset-disabled-card">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="w-4 h-4 text-muted-foreground" />Reset tidak tersedia
          </div>
          <p className="text-sm text-muted-foreground">
            {data.disabled_reason || "Reset Data dinonaktifkan."} Fitur tetap ada, namun tidak dapat
            dijalankan sampai Owner mengaktifkannya kembali (RESET_DATA_ENABLED=true).
          </p>
        </Card>
      ) : (
      <Card className="p-5 border-destructive/40 bg-destructive/5 space-y-3" data-testid="uat-reset-danger">
        <div className="flex items-center gap-2 text-destructive font-medium">
          <AlertTriangle className="w-4 h-4" />Zona Bahaya — Tindakan tidak dapat dibatalkan
        </div>
        <p className="text-sm text-muted-foreground">
          Ketik <b>{CONFIRM_PHRASE}</b> untuk mengonfirmasi. Dipertahankan: pengguna, role matrix,
          Owner, backup, riwayat migrasi, & audit reset.
        </p>
        <div className="flex flex-wrap gap-3">
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 placeholder={`Ketik ${CONFIRM_PHRASE}`} className="max-w-[220px] font-mono"
                 data-testid="uat-confirm-input" />
          <Button variant="destructive" onClick={() => setDlgOpen(true)} disabled={!canReset}
                  data-testid="uat-reset-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
            Reset Data Simulasi
          </Button>
        </div>
      </Card>
      )}

      {/* FIX-C: Konfirmasi BERLAPIS — ringkasan dampak NYATA sebelum eksekusi permanen. */}
      <AlertDialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <AlertDialogContent data-testid="uat-reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />Konfirmasi Penghapusan Permanen
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tindakan ini <b>tidak dapat dibatalkan</b>. Berikut ringkasan data yang akan
              dihapus permanen dari database <b>dan</b> arsip PDF di penyimpanan (R2):
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="grid grid-cols-2 gap-2 py-1" data-testid="uat-impact-summary">
            {HIGHLIGHTS.map(({ key, label, icon: Icon }) => (
              <div key={key} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <Icon className="w-4 h-4" />{label}
                </span>
                <span className="font-semibold tabular-nums" data-testid={`uat-impact-${key}`}>
                  {rt[key] ?? 0}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
              <span className="flex items-center gap-2 text-destructive">
                <FileText className="w-4 h-4" />PDF / objek R2
              </span>
              <span className="font-semibold tabular-nums text-destructive" data-testid="uat-impact-archive">
                {archiveCount}
              </span>
            </div>
          </div>

          <div className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground" data-testid="uat-impact-total">
            Total <b>{totalRows}</b> baris database + <b>{archiveCount}</b> objek arsip R2 akan dihapus.
            Dipertahankan: pengguna, role, master diskon, backup, migrasi, audit reset.
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="uat-reset-cancel-btn" disabled={busy}>Batal</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button variant="destructive" onClick={doReset} disabled={busy}
                      data-testid="uat-reset-confirm-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                Ya, Hapus Permanen
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
